
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  hasFullAccess: boolean;
  hasBuAccess: boolean;
  userName: string | null;
  userBu: string[];
}

const initialState: AuthContextType = {
  user: null,
  isLoading: true,
  hasFullAccess: false,
  hasBuAccess: false,
  userName: null,
  userBu: []
};

// Export the context so it can be imported
export const AuthContext = createContext<AuthContextType>(initialState);

// Create a custom hook for using the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [hasBuAccess, setHasBuAccess] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [userBu, setUserBu] = useState<string[]>([]);
  const { toast } = useToast();

  // Function to clear all auth-related localStorage items
  const clearAuthCache = () => {
    localStorage.removeItem('dashboard_authorized');
    localStorage.removeItem('dashboard_user_access');
    localStorage.removeItem('dashboard_user_name');
    localStorage.removeItem('dashboard_user_id');
    localStorage.removeItem('dashboard_user_bu');
  };

  // Function to fetch and update user profile data
  const updateUserProfile = async (userId: string, userEmail?: string) => {
    try {
      console.log('🔍 Auth Debug: Fetching user profile for auth_id:', userId);
      console.log('🔍 Auth Debug: User email:', userEmail);
      
      if (!userId) {
        console.log('🔍 Auth Debug: No user ID provided, cannot fetch profile');
        setHasFullAccess(false);
        setHasBuAccess(false);
        setUserName(null);
        setUserBu([]);
        return;
      }
      
      // Search by auth_id which is the reliable identifier
      console.log('🔍 Auth Debug: Querying kol_pic where auth_id =', userId);
      const { data: kolPicData, error } = await supabase
        .from('kol_pic')
        .select('access, name, bu, id, auth_id, email')
        .eq('auth_id', userId)
        .maybeSingle();
        
      console.log('🔍 Auth Debug: Query result:', { kolPicData, error });

      if (error) {
        console.error("Error fetching user profile:", error);
        
        // Show a helpful error message
        toast({
          title: "Account Setup Required",
          description: "Your account exists but needs to be set up. Please contact an administrator or use the debug panel to create your user record.",
          variant: "destructive"
        });
        
        // Set defaults for missing user with proper capitalization
        const fallbackName = userEmail?.split('@')[0] || 'User';
        const capitalizedName = fallbackName.charAt(0).toUpperCase() + fallbackName.slice(1).toLowerCase();
        
        setHasFullAccess(false);
        setHasBuAccess(false);
        setUserName(capitalizedName);
        setUserBu([]);
        return;
      }

      if (!kolPicData) {
        console.log('🔍 Auth Debug: No kol_pic record found for auth_id:', userId);
        
        toast({
          title: "Account Setup Required",
          description: "Your account exists but needs to be set up. Please contact an administrator or use the debug panel to create your user record.",
          variant: "destructive"
        });
        
        // Set defaults for missing user with proper capitalization
        const fallbackName = userEmail?.split('@')[0] || 'User';
        const capitalizedName = fallbackName.charAt(0).toUpperCase() + fallbackName.slice(1).toLowerCase();
        
        setHasFullAccess(false);
        setHasBuAccess(false);
        setUserName(capitalizedName);
        setUserBu([]);
        
        // Clear localStorage for missing user
        clearAuthCache();
        return;
      }

      // Update state with successful profile data
      console.log('🔍 Auth Debug: Profile found by auth_id');
      console.log('🔍 Auth Debug: Profile access level:', kolPicData?.access);
      setHasFullAccess(kolPicData?.access === 'full-access');
      setHasBuAccess(kolPicData?.access === 'bu-access');
      setUserName(kolPicData?.name || null);
      
      // Handle BU array
      let buArray: string[] = [];
      if (kolPicData?.bu) {
        if (Array.isArray(kolPicData.bu)) {
          buArray = kolPicData.bu.filter((item): item is string => typeof item === 'string');
        }
      }
      setUserBu(buArray);
      
      // Update localStorage with the new data
      console.log('🔍 Auth Debug: Updating localStorage with:', {
        access: kolPicData?.access,
        name: kolPicData?.name,
        userId: userId
      });
      
      localStorage.setItem('dashboard_user_access', kolPicData?.access || 'limited');
      localStorage.setItem('dashboard_user_name', kolPicData?.name || '');
      localStorage.setItem('dashboard_user_bu', JSON.stringify(buArray));
      localStorage.setItem('dashboard_authorized', 'true');
      localStorage.setItem('dashboard_user_id', userId);
      
      console.log('🔍 Auth Debug: localStorage updated. Current values:', {
        access: localStorage.getItem('dashboard_user_access'),
        name: localStorage.getItem('dashboard_user_name'),
        authorized: localStorage.getItem('dashboard_authorized')
      });
      
      console.log('🔍 Auth Debug: User profile updated successfully:', {
        access: kolPicData?.access,
        name: kolPicData?.name,
        bu: buArray,
        hasFullAccess: kolPicData?.access === 'full-access',
        hasBuAccess: kolPicData?.access === 'bu-access'
      });
      
    } catch (error) {
      console.error("🔍 Auth Debug: Unexpected error fetching user profile:", error);
      setHasFullAccess(false);
      setHasBuAccess(false);
      setUserName(null);
      setUserBu([]);
    }
  };

  // Function to refresh user data (called when access changes)
  const refreshUserData = async (userId: string, userEmail?: string) => {
    if (userEmail) {
      await updateUserProfile(userId, userEmail);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
        
        if (session?.user) {
          console.log('🔍 Auth Debug: Session found for user:', session.user.email);
          console.log('🔍 Auth Debug: User ID from session:', session.user.id);
          
          // For all users, use email for reliable matching
          setTimeout(() => {
            updateUserProfile(session.user.id, session.user.email || undefined);
          }, 0);
        } else {
          console.log('🔍 Auth Debug: No session found');
          // Clear user data if no session
          setHasFullAccess(false);
          setHasBuAccess(false);
          setUserName(null);
          setUserBu([]);
          clearAuthCache();
        }
      } catch (error) {
        console.error("Auth error:", error);
        setUser(null);
        setHasFullAccess(false);
        setHasBuAccess(false);
        setUserName(null);
        setUserBu([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔍 Auth Debug: Auth state changed:', event, session?.user?.email);
        console.log('🔍 Auth Debug: User ID from state change:', session?.user?.id);
        
        setUser(session?.user || null);
        
        if (session?.user) {
          // For all users, use email for reliable matching
          setTimeout(() => {
            updateUserProfile(session.user.id, session.user.email || undefined);
          }, 0);
        } else {
          // Clear user data on sign out
          setHasFullAccess(false);
          setHasBuAccess(false);
          setUserName(null);
          setUserBu([]);
          clearAuthCache();
        }
        
        setIsLoading(false);
      }
    );

    checkAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, [toast]);

  return (
    <AuthContext.Provider value={{ user, isLoading, hasFullAccess, hasBuAccess, userName, userBu }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
